import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { DomainsPanel, Org, SharedWith, tenantLine, useOrganizations } from "./Organizations";
import { SectionHead, ShowMore, useShowMore } from "./Section";
import { ErrorState, PageSkeleton } from "../shell/states";
import { useQueryParam } from "../urlState";

// Administration → Districts: every district, the real schools each owns, its claimed domains, and
// creating one (docs/ADMIN.md step 4c; the Access Map plan, change 4a). The other tenants — Public
// and the reference corpus — are not districts; the Platform screen lists every tenant, so nothing
// is hidden, only placed. For an operator; the server refuses anyone else.
//
// Schools are FOUND, never made: they come from the directory of real schools the state-data
// import keeps, keyed by federal (NCES) ids. Creating a district from a real NCES district links
// every school listed under it at once. A school already in another district says so and cannot
// be linked — one district per school.

interface District {
  tenant_id: string;
  name: string;
  tenant_type: string;
  jurisdiction: string | null;
  schools: number;
  // Whether schools can be linked to it: a district can; the corpus and `public` cannot.
  holds_schools: boolean;
}

/** Why a tenant holds no schools, when it cannot. Pure, so it is tested. */
export function whyNoSchools(d: Pick<District, "name" | "tenant_type">): string {
  if (d.tenant_type === "corpus") {
    return `${d.name} holds reference papers, not a school's students, so no school can be `
      + "linked to it: that would let those papers claim a real school.";
  }
  if (d.tenant_type === "public") {
    return `${d.name} holds publisher content every district starts from, so no school can be `
      + "linked to it.";
  }
  return `${d.name} is a ${d.tenant_type}, not a district, so no school can be linked to it.`;
}

interface School {
  school_id: string;
  school_name: string | null;
  district_name: string | null;
  county_name: string | null;
  school_level: string | null;
  belongs_to?: string | null;
}

interface NcesDistrict {
  district_id: string;
  district_name: string | null;
  county_name: string | null;
  schools: number;
}

interface Created {
  tenant_id: string;
  name: string;
  linked: number;
  refused: { school_id: string; why: string }[];
}

const said = (e: unknown) => e instanceof ApiError ? (e.detail ?? e.message) : "That didn't work.";

/** What creating a district did, in one sentence. Pure, so it is tested. */
export function createdSentence(c: Created): string {
  const schools = c.linked === 1 ? "1 school" : `${c.linked} schools`;
  const base = c.linked ? `Created ${c.name} and linked ${schools}.` : `Created ${c.name}.`;
  if (!c.refused.length) return base;
  const n = c.refused.length;
  return `${base} ${n === 1 ? "1 school was" : `${n} schools were`} not linked: `
    + c.refused.map((r) => r.why).join("; ") + ".";
}

// What the directory search is doing, so it never answers with silence.
type Search = "idle" | "searching" | "done" | "failed";

/** What a finished search says when it found nothing, or failed. Pure, so it is tested. */
export function searchSays(state: Search, term: string, what: "school" | "district",
                           found: number): string | null {
  if (state === "failed") return "The directory could not be searched just now. Try again.";
  if (state !== "done" || found > 0) return null;
  return `No ${what} in the directory matches “${term.trim()}”. The directory holds `
    + "California's public schools, from the state data import.";
}

function useDirectory(q: string) {
  const [found, setFound] = useState<{ schools: School[]; districts: NcesDistrict[] }>(
    { schools: [], districts: [] });
  const [state, setState] = useState<Search>("idle");
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setFound({ schools: [], districts: [] }); setState("idle"); return; }
    setState("searching");
    let live = true;
    const t = setTimeout(() => {
      api.get<{ schools: School[]; districts: NcesDistrict[] }>(
        `/directory?q=${encodeURIComponent(term)}`)
        .then((d) => { if (live) { setFound(d); setState("done"); } })
        .catch(() => { if (live) { setFound({ schools: [], districts: [] }); setState("failed"); } });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return { ...found, state };
}

export function Districts() {
  const [districts, setDistricts] = useState<District[] | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [open, setOpen] = useQueryParam("district");
  const [creating, setCreating] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const organizations = useOrganizations();
  const orgOf = (tenantId: string): Org | undefined => organizations.data?.organizations.find(
    (o) => o.kind === "district" && o.home_tenant_id === tenantId);

  const load = useCallback(() => {
    api.get<{ districts: District[] }>("/districts")
      .then((d) => setDistricts(d.districts ?? []))
      .catch((e: unknown) => setError(e instanceof ApiError
        ? { status: e.status, message: e.detail ?? e.message }
        : { status: 0, message: "could not be read" }));
  }, []);
  useEffect(load, [load]);

  if (error?.status === 403) {
    return (
      <div className="gl">
        <h2>Districts</h2>
        <p className="gl-lede">Only an operator can see and create districts.</p>
      </div>
    );
  }
  if (error) return <ErrorState />;
  if (!districts) return <PageSkeleton />;
  const shown = districts.filter((d) => d.holds_schools);

  return (
    <div className="gl pp">
      <SectionHead title="Districts" level={2} addLabel="New district" adding={creating}
                   onAdd={() => { setCreating(!creating); setNote(null); }} />
      {note && <p className="pp-note">{note}</p>}
      {creating && (
        <NewDistrict onCancel={() => setCreating(false)}
                     onCreated={(c) => {
                       setCreating(false); setNote(createdSentence(c)); load();
                       organizations.reload();
                     }} />
      )}
      {shown.length === 0 ? (
        <p className="rv-mut">No districts yet.</p>
      ) : (
        <table className="tbl">
          <thead>
            <tr><th>District</th><th>Id</th><th>Type</th><th className="r">Schools</th><th /></tr>
          </thead>
          <tbody>
            {shown.map((d) => [
              <tr key={d.tenant_id}>
                <td>{d.name}</td>
                <td><code>{d.tenant_id}</code></td>
                <td>{d.tenant_type}</td>
                <td className="r">{d.schools}</td>
                <td className="r">
                  <button className="btn-link" aria-expanded={open === d.tenant_id}
                          onClick={() => setOpen(open === d.tenant_id ? null : d.tenant_id)}>
                    {open === d.tenant_id ? "Hide" : "Open"}
                  </button>
                </td>
              </tr>,
              open === d.tenant_id && (
                <tr key={`${d.tenant_id}:schools`} className="pp-add">
                  <td colSpan={5}>
                    {orgOf(d.tenant_id) && (
                      <p className="rv-mut">{tenantLine(orgOf(d.tenant_id)!)}</p>
                    )}
                    <DistrictSchools district={d} onChanged={() => {
                      load(); organizations.reload();
                    }} />
                    {orgOf(d.tenant_id) && organizations.data && (
                      <SharedWith org={orgOf(d.tenant_id)!}
                                  orgs={organizations.data.organizations}
                                  onChanged={organizations.reload} />
                    )}
                    {orgOf(d.tenant_id) && organizations.data && (
                      <DomainsPanel org={orgOf(d.tenant_id)!}
                                    choices={organizations.data.sign_in_choices}
                                    onChanged={organizations.reload} />
                    )}
                  </td>
                </tr>
              ),
            ])}
          </tbody>
        </table>
      )}
    </div>
  );
}

function NewDistrict({ onCreated, onCancel }: {
  onCreated: (c: Created) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [q, setQ] = useState("");
  const [nces, setNces] = useState<NcesDistrict | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const found = useDirectory(nces ? "" : q);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      onCreated(await api.post<Created>("/districts", {
        name: name.trim(), nces_district_id: nces?.district_id ?? null }));
    } catch (e) {
      setError(said(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pp-panel add-form">
      <label className="pp-field">
        A real district, to link its schools (optional)
        {nces ? (
          <span className="pp-chosen">
            {nces.district_name} · NCES {nces.district_id} · {nces.schools} schools
            <button className="btn-link" onClick={() => { setNces(null); setQ(""); }}>change</button>
          </span>
        ) : (
          <input type="search" value={q} placeholder="search by district name or NCES id"
                 onChange={(e) => setQ(e.target.value)} />
        )}
      </label>
      {!nces && found.state === "searching" && <p className="rv-mut dots">searching</p>}
      {!nces && searchSays(found.state, q, "district", found.districts.length) && (
        <p className="rv-mut">{searchSays(found.state, q, "district", found.districts.length)}</p>
      )}
      {!nces && found.districts.length > 0 && (
        <ul className="pp-results">
          {found.districts.map((d) => (
            <li key={d.district_id}>
              <button className="btn-link" onClick={() => {
                setNces(d); if (!name.trim()) setName(d.district_name ?? "");
              }}>
                {d.district_name}
              </button>
              <span className="rv-mut"> · {d.county_name} · NCES {d.district_id} · {d.schools} schools</span>
            </li>
          ))}
        </ul>
      )}
      <label className="pp-field">
        Name
        <input value={name} onChange={(e) => setName(e.target.value)}
               placeholder="as the district calls itself" />
      </label>
      <div className="pp-actions">
        <button className="btn primary sm" disabled={busy || !name.trim()} onClick={create}>
          Create district
        </button>
        <button className="btn sm" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      {error && <p className="inc-error">{error}</p>}
    </div>
  );
}

function DistrictSchools({ district, onChanged }: { district: District; onChanged: () => void }) {
  const [schools, setSchools] = useState<School[] | null>(null);
  const rows = useShowMore();
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const found = useDirectory(adding ? q : "");

  const load = useCallback(() => {
    api.get<{ schools: School[] }>(`/districts/${encodeURIComponent(district.tenant_id)}/schools`)
      .then((d) => setSchools(d.schools ?? []))
      .catch((e: unknown) => setError(said(e)));
  }, [district.tenant_id]);
  useEffect(load, [load]);

  async function link(s: School) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/districts/${encodeURIComponent(district.tenant_id)}/schools`,
                     { school_id: s.school_id });
      setQ("");
      setAdding(false);
      load();
      onChanged();
    } catch (e) {
      setError(said(e));
    } finally {
      setBusy(false);
    }
  }

  if (!schools) return <p className="muted dots">Loading schools</p>;
  return (
    <div className="pp-panel">
      <SectionHead title="Schools" addLabel={`Add a school to ${district.name}`} adding={adding}
                   onAdd={() => { setAdding(!adding); setQ(""); setError(null); }}
                   disabledWhy={district.holds_schools ? null : whyNoSchools(district)} />
      {adding && (
        <div className="add-form">
          <label className="pp-field">
            Find the school in the directory of real schools
            <input type="search" value={q} placeholder="type part of its name, or its NCES id"
                   autoFocus onChange={(e) => setQ(e.target.value)} />
          </label>
          {found.state === "searching" && <p className="rv-mut dots">searching</p>}
          {searchSays(found.state, q, "school", found.schools.length) && (
            <p className="rv-mut">{searchSays(found.state, q, "school", found.schools.length)}</p>
          )}
        </div>
      )}
      {schools.length === 0 ? (
        <p className="rv-mut">None yet.</p>
      ) : (
        <table className="tbl">
          <thead><tr><th>School</th><th>Level</th><th>County</th><th>NCES id</th></tr></thead>
          <tbody>
            {schools.slice(0, rows.limit).map((s) => (
              <tr key={s.school_id}>
                <td>{s.school_name}</td>
                <td>{s.school_level ?? ""}</td>
                <td>{s.county_name ?? ""}</td>
                <td><code>{s.school_id}</code></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ShowMore shown={Math.min(rows.limit, schools.length)} total={schools.length} onMore={rows.more} />
      {adding && found.schools.length > 0 && (
        <ul className="pp-results">
          {found.schools.map((s) => {
            const here = s.belongs_to === district.tenant_id;
            const elsewhere = !!s.belongs_to && !here;
            return (
              <li key={s.school_id}>
                {s.school_name}
                <span className="rv-mut"> · {s.district_name} · NCES {s.school_id}</span>
                {here ? <span className="rv-mut"> · already here</span>
                  : elsewhere ? <span className="rv-mut"> · belongs to {s.belongs_to}</span>
                  : <button className="btn-link" disabled={busy} onClick={() => link(s)}>Add</button>}
              </li>
            );
          })}
        </ul>
      )}
      {error && <p className="inc-error">{error}</p>}
    </div>
  );
}
